import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export default class Camera {
  constructor(renderer, options = {}) {
    this.renderer = renderer;
    
    // Camera animation properties (from your App.js)
    this.isMouseUp = true;
    this.cameraVelocity = new THREE.Vector3();
    
    // Camera parameters with defaults
    this.fov = options.fov || 45;
    this.aspect = options.aspect || window.innerWidth / window.innerHeight;
    this.near = options.near || 0.1;
    this.far = options.far || 1000;
    
    // Camera position with defaults
    this.position = options.position || { x: 0, y: 2, z: 5 };
    
    // Control settings with defaults (from your App.js)
    this.controlSettings = {
      enableDamping: options.enableDamping !== undefined ? options.enableDamping : true,
      dampingFactor: options.dampingFactor || 0.02,
      screenSpacePanning: options.screenSpacePanning !== undefined ? options.screenSpacePanning : false,
      minDistance: options.minDistance || 10,
      maxDistance: options.maxDistance || 15,
      enablePan: options.enablePan !== undefined ? options.enablePan : false,
      enableZoom: options.enableZoom !== undefined ? options.enableZoom : true
    };
    
    this.setupCamera();
    this.setupControls();
    this.setupEventListeners();
    this.startCameraAnimation();
  }

  setupCamera() {
    this.camera = new THREE.PerspectiveCamera(
      this.fov,
      this.aspect,
      this.near,
      this.far
    );
    this.camera.position.set(this.position.x, this.position.y, this.position.z);
  }

  setupControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = this.controlSettings.enableDamping;
    this.controls.dampingFactor = this.controlSettings.dampingFactor;
    this.controls.screenSpacePanning = this.controlSettings.screenSpacePanning;
    this.controls.minDistance = this.controlSettings.minDistance;
    this.controls.maxDistance = this.controlSettings.maxDistance;
    this.controls.enablePan = this.controlSettings.enablePan;
    this.controls.enableZoom = this.controlSettings.enableZoom;
    this.controls.update();
  }

  setupEventListeners() {
    // Mouse events for camera animation (from your App.js)
    document.addEventListener("mousedown", () => {
      this.isMouseUp = false;
    });

    document.addEventListener("mouseup", () => {
      this.isMouseUp = true;
    });

    // Window resize
    window.addEventListener("resize", () => this.onWindowResize());
  }

  animateCamera() {
    if (!this.isMouseUp) {
      this.cameraVelocity.set(0, 0, 0);
    } else {
      const dampingFactor = 1.2;
      this.cameraVelocity.multiplyScalar(dampingFactor);
    }

    this.controls.update();
    this.camera.position.add(this.cameraVelocity);
    requestAnimationFrame(() => this.animateCamera());
  }

  startCameraAnimation() {
    this.animateCamera();
  }

  onWindowResize() {
    this.aspect = window.innerWidth / window.innerHeight;
    this.camera.aspect = this.aspect;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  // Getter to access the camera instance
  getCamera() {
    return this.camera;
  }

  // Getter to access the controls
  getControls() {
    return this.controls;
  }

  // Method to set camera position
  setPosition(x, y, z) {
    this.camera.position.set(x, y, z);
    this.controls.update();
  }

  // Method to look at a specific point
  lookAt(x, y, z) {
    this.camera.lookAt(x, y, z);
  }

  // Method to update control settings
  updateControlSettings(settings = {}) {
    if (settings.minDistance !== undefined) this.controls.minDistance = settings.minDistance;
    if (settings.maxDistance !== undefined) this.controls.maxDistance = settings.maxDistance;
    if (settings.enablePan !== undefined) this.controls.enablePan = settings.enablePan;
    if (settings.enableZoom !== undefined) this.controls.enableZoom = settings.enableZoom;
    if (settings.dampingFactor !== undefined) this.controls.dampingFactor = settings.dampingFactor;
  }

  // Method to update controls (call this in your animation loop)
  update() {
    this.controls.update();
  }

  // Animated zoom functionality
  animatedZoom(targetDistance, duration = 5000) {
    const startDistance = this.camera.position.distanceTo(this.controls.target);
    const startPosition = this.camera.position.clone();
    const direction = startPosition.clone().sub(this.controls.target).normalize();
    const targetPosition = this.controls.target.clone().add(direction.multiplyScalar(targetDistance));
    
    const startTime = Date.now();
    
    const animateZoom = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Very strong ease-out for smooth, slow ending
      const eased = 1 - Math.pow(1 - progress, 2);
      
      // Interpolate position
      const currentPosition = startPosition.clone().lerp(targetPosition, eased);
      this.camera.position.copy(currentPosition);
      this.controls.update();
      
      if (progress < 1) {
        requestAnimationFrame(animateZoom);
      }
    };
    
    animateZoom();
  }

  // Toggle between normal and zoomed view
  toggleZoom(closeDistance = 3, normalDistance = 8, duration = 2500) {
    const currentDistance = this.camera.position.distanceTo(this.controls.target);
    const isZoomedIn = currentDistance < (closeDistance + normalDistance) / 2;
    
    if (isZoomedIn) {
      this.animatedZoom(normalDistance, duration);
    } else {
      this.animatedZoom(closeDistance, duration);
    }
  }
}
